import { EditOutlined } from "@ant-design/icons"
import { Avatar, Button, Flex, Form, Modal, Popover, Tag, Typography } from "antd"
import { type BaseOptionType } from "antd/lib/select"
import React, { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import { ResolvedSelect } from "../../../../../components/Select/ResolvedSelect.tsx"
import {
  UNRESOLVED_USER_LABEL,
  buildEntityLabelMap
} from "../../../../../utils/select/entity-labels.ts"
import { mergeSelectedIntoOptions } from "../../../../../utils/select/resolve-options.ts"
import { getEntityName } from "../../../../../utils/user/get-entity-name"
import { type LeadSalesManager } from "../../../types/lead.types.ts"
import { type LeadHeaderPatch } from "./lead-header-patch.ts"

const { Text } = Typography

interface OwnerFormValues {
  sales_manager?: string[]
  primary?: string | null
}

interface LeadWorkspaceOwnerProps {
  salesManagers?: LeadSalesManager[] | null
  owners: BaseOptionType[]
  canAssign?: boolean
  saving?: boolean
  /** Named by a rejected stage transition; the label carries the mark. */
  missing?: boolean
  onChange: (patch: LeadHeaderPatch) => Promise<boolean>
}

export function LeadWorkspaceOwner({
  salesManagers,
  owners,
  canAssign,
  saving,
  missing,
  onChange
}: LeadWorkspaceOwnerProps) {
  const { t } = useTranslation()
  const [form] = Form.useForm<OwnerFormValues>()
  const [open, setOpen] = useState(false)

  const assigned = useMemo(() => salesManagers ?? [], [salesManagers])
  const sortedByPrimary = useMemo(
    () => [...assigned].sort((a, b) => Number(b?.is_main) - Number(a?.is_main)),
    [assigned]
  )
  const [primary, ...rest] = sortedByPrimary

  const fallbackLabels = useMemo(
    () => buildEntityLabelMap(assigned),
    [assigned]
  )

  const watched = Form.useWatch("sales_manager", form)
  const selectedIds = watched ?? assigned.map((sales) => sales.id)

  // A rep the options list cannot resolve is still assigned, so merging the
  // picked ids back in is what keeps them choosable as primary.
  const primaryOptions = useMemo(
    () =>
      mergeSelectedIntoOptions(
        owners.filter((owner) => selectedIds.includes(String(owner?.value))),
        selectedIds,
        fallbackLabels,
        UNRESOLVED_USER_LABEL
      ),
    [owners, selectedIds, fallbackLabels]
  )

  // Only a set of two or more leaves anything to choose: the leads list
  // promotes a lone representative the same way rather than asking.
  const needsPrimary = selectedIds.length > 1

  const openEditor = () => {
    form.setFieldsValue({
      sales_manager: assigned.map((sales) => sales.id),
      primary: assigned.find((sales) => sales?.is_main)?.id ?? null
    })
    setOpen(true)
  }

  const closeEditor = () => {
    setOpen(false)
    form.resetFields()
  }

  const syncPrimary = (ids: string[]) => {
    if (ids.length === 1) return form.setFieldValue("primary", ids[0])

    const current = form.getFieldValue("primary")
    if (current && !ids.includes(current)) form.setFieldValue("primary", null)
  }

  const submit = async () => {
    let values: OwnerFormValues
    try {
      values = await form.validateFields()
    } catch {
      return
    }

    const ids = values.sales_manager ?? []
    const main = ids.length === 1 ? ids[0] : values.primary

    const next = ids.map((id) => ({ id, is_main: id === main }))

    if (!(await onChange({ sales_manager: next }))) return

    closeEditor()
  }

  return (
    <Flex
      vertical
      gap={2}
      data-testid={"lead-header-owner"}
      data-missing={missing ? "true" : undefined}
    >
      <Text type={missing ? "danger" : "secondary"} className={"text-xs"}>
        {t("leadWorkspace.header.owner.label")}
      </Text>
      <Flex gap={6} align={"center"}>
        {primary ? (
          <>
            <Avatar size={"small"} src={primary?.avatar} className={"shrink-0"}>
              {primary?.first_name?.charAt(0)}
            </Avatar>
            <Text className={"min-w-0"} ellipsis>
              {getEntityName(primary)}
            </Text>
            {!!rest.length && (
              <Popover
                trigger={"click"}
                title={t("leadWorkspace.header.owner.popoverTitle")}
                content={
                  <Flex vertical gap={8} className={"min-w-40"}>
                    {sortedByPrimary.map((sales) => (
                      <Flex
                        key={sales.id}
                        gap={8}
                        align={"center"}
                        justify={"space-between"}
                      >
                        <Flex gap={6} align={"center"} className={"min-w-0"}>
                          <Avatar size={"small"} src={sales?.avatar}>
                            {sales?.first_name?.charAt(0)}
                          </Avatar>
                          <Text className={"min-w-0"} ellipsis>
                            {getEntityName(sales)}
                          </Text>
                        </Flex>
                        {sales?.is_main && (
                          <Text type={"secondary"} className={"text-xs shrink-0"}>
                            {t("leadWorkspace.header.owner.primaryTag")}
                          </Text>
                        )}
                      </Flex>
                    ))}
                  </Flex>
                }
              >
                <Tag
                  className={"m-0 shrink-0 cursor-pointer"}
                  aria-label={t("leadWorkspace.header.owner.viewAll")}
                >{`+${rest.length}`}</Tag>
              </Popover>
            )}
          </>
        ) : (
          <Text type={"secondary"}>
            {t("leadWorkspace.header.owner.unassigned")}
          </Text>
        )}

        {canAssign && (
          <Button
            size={"small"}
            type={"text"}
            aria-label={t("leadWorkspace.header.owner.change")}
            icon={<EditOutlined />}
            onClick={openEditor}
          />
        )}
      </Flex>

      {canAssign && (
        <Modal
          open={open}
          destroyOnHidden
          maskClosable={false}
          title={t("leadWorkspace.header.owner.title")}
          okText={t("leadWorkspace.header.save")}
          cancelText={t("leadWorkspace.header.cancel")}
          confirmLoading={saving}
          onOk={submit}
          onCancel={closeEditor}
        >
          <Form form={form} layout={"vertical"}>
            <Form.Item<OwnerFormValues>
              name={"sales_manager"}
              label={t("leadWorkspace.header.owner.assigned")}
            >
              <ResolvedSelect
                showSearch
                allowClear
                mode={"multiple"}
                optionFilterProp={"label"}
                options={owners}
                fallbackLabels={fallbackLabels}
                fallbackLabel={UNRESOLVED_USER_LABEL}
                placeholder={t("leadWorkspace.header.owner.placeholderMany")}
                onChange={syncPrimary}
              />
            </Form.Item>

            {needsPrimary && (
              <Form.Item<OwnerFormValues>
                preserve={false}
                name={"primary"}
                label={t("leadWorkspace.header.owner.primary")}
                rules={[
                  {
                    required: true,
                    message: t("leadWorkspace.header.validation.primaryOwner")
                  }
                ]}
              >
                <ResolvedSelect
                  showSearch
                  optionFilterProp={"label"}
                  options={primaryOptions}
                  fallbackLabels={fallbackLabels}
                  fallbackLabel={UNRESOLVED_USER_LABEL}
                  placeholder={t("leadWorkspace.header.owner.placeholder")}
                />
              </Form.Item>
            )}
          </Form>
        </Modal>
      )}
    </Flex>
  )
}
